import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@coin/shared/core/util-environments';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, retryWhen, tap } from 'rxjs/operators';
import {
  GenericSimulationResponse,
  PaginatedResult,
  RuleSetV2,
  SimpleTableService,
  TransactionStatus,
  TransactionStatusMetadata
} from '@coin/shared/core/util-models';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { checkResult, encodeSeasonType, pollingStrategy, RuleEngineHelper } from '@coin/shared/core/util-helpers';
import { Season } from '@coin/shared/season-mgmt/util';
import { GenericExportResponse } from '../models/generic-export-response.model';

@Injectable({
  providedIn: 'root'
})
export class GeneralSeasonService implements SimpleTableService {
  private httpClient = inject(HttpClient);
  private toast = inject(ToastrService);
  private loadingService = inject(LoadingService);
  private httpHelper = inject(HttpHelpersService);

  public queryPagedAndFilteredData = (page: number, size: number): Observable<PaginatedResult<Season>> =>
    this.getSeasons(page, size, SeasonTypeEnum.SOG);

  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public getSeasons(page: number, size: number, seasonType: SeasonTypeEnum): Observable<PaginatedResult<Season>> {
    if (seasonType === SeasonTypeEnum.Restructuring) {
      return this.getRestructuringSeasons(page, size);
    }

    const isGBR = seasonType === 'GBR';
    const baseUrl = isGBR
      ? `${environment.api.baseUrl}/admin/api/v2/master/seasons`
      : `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/seasons`;

    const url = `${baseUrl}?page=${page + 1}&size=${size}&seasonType=${seasonType}`;

    return this.httpClient.get<PaginatedResult<Season>>(url).pipe(catchError(() => of(null)));
  }

  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public getSeasonById(id: string, seasonType?: SeasonTypeEnum): Observable<Season> {
    this.loadingService.present();

    const isGBR = seasonType === 'GBR';
    const url = isGBR
      ? `${environment.api.baseUrl}/admin/api/v2/master/seasons/${id}`
      : `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType!)}/seasons/${id}`;

    return this.httpClient.get<Season>(url).pipe(
      map(checkResult()),
      retryWhen(pollingStrategy({ timerDuration: 500 })),
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error retrieving season. ${res?.error?.reason || 'Unknown'} `);
        return throwError(() => new Error(`Error retrieving season. ${res?.error?.reason || 'Unknown'} `));
      }),
      finalize(() => this.loadingService.dismiss())
    );
  }

  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public createSeason(body: Season): Observable<Season> {
    this.loadingService.present();

    const isGBR = body.seasonType === 'GBR';
    const url = isGBR
      ? `${environment.api.baseUrl}/admin/api/v2/master/seasons`
      : `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(body.seasonType)}/seasons`;

    return this.httpClient.post<Season>(url, body).pipe(
      tap(() => {
        this.toast.success(`Season "${body.title}" created.`);
      }),
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error creating season. ${res?.error?.reason || 'Unknown'} `);
        return throwError(() => new Error(`Error creating season. ${res?.error?.reason || 'Unknown'} `));
      }),
      finalize(() => this.loadingService.dismiss())
    );
  }

  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public updateSeason(body: Season): Observable<Season> {
    this.loadingService.present();

    const isGBR = body.seasonType === 'GBR';
    const url = isGBR
      ? `${environment.api.baseUrl}/admin/api/v2/master/seasons`
      : `${environment.api.baseUrl}/admin/api/v1/merit/seasons`;

    return this.httpClient.put<Season>(url, body).pipe(
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error updating season. ${res?.error?.reason || 'Unknown'} `);
        return throwError(() => new Error(`Error updating season. ${res?.error?.reason || 'Unknown'} `));
      }),
      finalize(() => this.loadingService.dismiss())
    );
  }

  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public getSimulatedPopulation(
    body: RuleSetV2,
    take: number,
    skip: number,
    seasonId: string,
    seasonType?: SeasonTypeEnum
  ): Observable<GenericSimulationResponse> {
    const page = !skip ? 1 : Math.floor(skip / take) + 1;
    const isGBR = seasonType === 'GBR';

    const url = isGBR
      ? `${environment.api.baseUrl}/admin/api/v2/master/seasons/${seasonId}/simulate-population?page=${page}&size=${take}`
      : `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType!)}/seasons/${seasonId}/simulate-population?page=${page}&size=${take}`;

    return this.httpClient.post<GenericSimulationResponse>(url, RuleEngineHelper.ruleSetV2ToDto(body)).pipe(
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Simulation request error: ${res.error?.message || JSON.stringify(res.error)}`);
        return throwError(() => new Error(`Error simulating ruleset. ${res?.error?.reason || 'Unknown'} `));
      })
    );
  }

  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public getSimulatedPopulationExport(
    body: RuleSetV2,
    seasonId: string,
    seasonType?: SeasonTypeEnum
  ): Observable<GenericSimulationResponse> {
    const isGBR = seasonType === 'GBR';

    const url = isGBR
      ? `${environment.api.baseUrl}/admin/api/v2/master/seasons/${seasonId}/simulate-population?page=1&size=1000000&export=true`
      : `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType!)}/seasons/${seasonId}/simulate-population?page=1&size=1000000&export=true`;

    return this.httpClient
      .post<GenericSimulationResponse>(url, RuleEngineHelper.ruleSetV2ToDto(body))
      .pipe(this.httpHelper.handleError('Cannot get simulated population'));
  }

  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public getSeasonMembers(seasonId: string, seasonType?: SeasonTypeEnum): Observable<GenericSimulationResponse> {
    const isGBR = seasonType === 'GBR';

    const url = isGBR
      ? `${environment.api.baseUrl}/admin/api/v2/master/seasons/${seasonId}/export-population`
      : `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType!)}/seasons/${seasonId}/export-population`;

    return this.httpClient.get<GenericExportResponse>(url).pipe(
      map(data => ({
        entitiesAdded: { content: data.exportedEntities },
        entitiesNotAdded: { content: [] }
      })),
      this.httpHelper.handleError('Cannot get season members')
    );
  }

  public getOMExport(
    seasonId: string,
    seasonType: SeasonTypeEnum
  ): Observable<GenericSimulationResponse> {

    const url = `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/seasons/${seasonId}/export-om`;

    return this.httpClient.get<GenericExportResponse>(url).pipe(
      map(data => ({
        entitiesAdded: { content: data.exportedEntities },
        entitiesNotAdded: { content: [] }
      })),
      this.httpHelper.handleError('Cannot export OM data')
    );
  }

  public getNonHeadPositionsExport(
    seasonId: string,
    seasonType: SeasonTypeEnum
  ): Observable<GenericSimulationResponse> {

    const url = `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/seasons/${seasonId}/export-non-head-positions`;

    return this.httpClient.get<GenericExportResponse>(url).pipe(
      map(data => ({
        entitiesAdded: { content: data.exportedEntities },
        entitiesNotAdded: { content: [] }
      })),
      this.httpHelper.handleError('Cannot export Non-Head Positions data')
    );
  }


  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public savePopulation(
    seasonId: string,
    seasonType?: SeasonTypeEnum
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    this.loadingService.present();

    const isGBR = seasonType === 'GBR';
    const url = isGBR
      ? `${environment.api.baseUrl}/admin/api/v2/master/seasons/${seasonId}/save-population`
      : `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType!)}/seasons/${seasonId}/save-population`;

    return this.httpClient.post<TransactionStatus<TransactionStatusMetadata>>(url, { id: seasonId }).pipe(
      this.httpHelper.handleError('Cannot save population'),
      finalize(() => this.loadingService.dismiss())
    );
  }

  // To Do: Revert seasontype check after GBR will be included into the season specific controllers (backend)
  public updateBenchmarkData(
    seasonId: string,
    seasonType?: SeasonTypeEnum,
    employeeIds?: string[]
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    this.loadingService.present();

    const isGBR = seasonType === 'GBR';
    const url = isGBR
      ? `${environment.api.baseUrl}/admin/api/v3/master/seasons/snapshots/benchmarks`
      : `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType!)}/snapshots/benchmarks`;

    return this.httpClient
      .patch<TransactionStatus<TransactionStatusMetadata>>(url, {
        seasonId,
        employeeIds
      })
      .pipe(
        this.httpHelper.handleError('Cannot update benchmark data'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  private getRestructuringSeasons(page: number, size: number): Observable<PaginatedResult<Season>> {
    return this.httpClient
      .get<
        PaginatedResult<Season>
      >(`${environment.api.baseUrl}/restructuring/admin/v1/seasons?page=${page + 1}&size=${size}`)
      .pipe(catchError(() => of(null)));
  }

}
